import React, { useState } from 'react';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import { Botao, Card, Chip, EmptyState, ErrorState, PageHeader } from '../../../ui';

import { Associado, fetchAssociadoPorCpfCnpj } from '../../../api/associado/associadoApi';
import { formatCpfCnpj } from '../../../utils/formartCPF';
import { rotuloSituacaoCarro } from '../../../utils/carroSituacao';

/**
 * `/adesoes/consultar` — consulta read-only de um associado pelo documento
 * (migrada para o design system na at-56, frente B).
 *
 * 🔴 **É a ÚNICA tela do consultor que mostra o histórico de veículos** — os
 * substituídos e os excluídos, e não apenas os em circulação (spec 02 §5). Foi
 * ela que ganhou o chip de situação em 09/2026, por decisão de Victor de que
 * veículo substituído deve ficar visível em algum lugar.
 *
 * 📌 **Até a at-56 nenhuma tela levava até aqui** — só dava para chegar digitando
 * a URL. O acesso passou a sair de `/adesoes` (decisão de Victor, 09/09/2026).
 *
 * ⚠️ Duas correções de comportamento nesta migração:
 *   1. **"não existe" ≠ "a busca falhou"** — havia um texto só para os dois
 *      ("Não encontrado ou erro na busca."), então indisponibilidade do backend
 *      era lida como CPF inexistente. Agora o 404 tem tratamento próprio.
 *   2. Saiu um **`console.log(responseAssociate)`** que despejava o associado
 *      inteiro no console do navegador — nome, CPF, RG e endereço. Além de
 *      `console.log` ser proibido em código novo, isso é **PII em log**.
 */

type Estado = 'inicial' | 'buscando' | 'achou' | 'nao-encontrado' | 'erro';

const AdesaoConsultar: React.FC = () => {
  const voltar = useVoltar();

  const [documento, setDocumento] = useState('');
  const [estado, setEstado] = useState<Estado>('inicial');
  const [erro, setErro] = useState('');
  const [associado, setAssociado] = useState<Associado | null>(null);

  const digitos = documento.replace(/\D/g, '');
  const podeBuscar = digitos.length > 0 && estado !== 'buscando';

  const buscar = async () => {
    if (!podeBuscar) return;

    setEstado('buscando');
    setErro('');
    setAssociado(null);
    try {
      const resposta = await fetchAssociadoPorCpfCnpj(digitos);
      setAssociado(resposta);
      setEstado('achou');
    } catch (excecao) {
      // `fetchAssociadoPorCpfCnpj` relança o erro do axios cru, então o status
      // está disponível — é o que permite separar os dois casos.
      const status = (excecao as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setEstado('nao-encontrado');
        return;
      }
      setErro(
        (excecao as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Tente de novo em alguns instantes.',
      );
      setEstado('erro');
    }
  };

  const carros = associado?.carros ?? [];

  return (
    <ShellSaac cabecalho={<PageHeader titulo="Consultar por CPF/CNPJ" aoVoltar={voltar} />}>
      <div className="tw-min-h-full tw-rounded-t-3xl tw-bg-shell lg:tw-rounded-none">
        <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-3xl tw-flex-col tw-gap-4 tw-px-3 tw-py-4 sm:tw-px-4 lg:tw-px-8 lg:tw-py-6">
          <Card className="tw-p-4">
            <label
              htmlFor="consulta-documento"
              className="tw-mb-1.5 tw-block tw-font-marca tw-text-sm tw-font-bold tw-text-texto-titulo"
            >
              CPF/CNPJ do associado
            </label>

            <div className="tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row">
              <input
                id="consulta-documento"
                /*
                 * ⚠️ `tel`, e não `text`, por DOIS motivos:
                 *   1. teclado numérico no celular — o consultor digita CPF em
                 *      campo, com uma mão;
                 *   2. `ui/tokens/legado-compartilhado.css` declara
                 *      `input[type="text"]` como seletor GLOBAL de elemento, que
                 *      vence os utilitários do Tailwind por especificidade
                 *      ((0,1,1) × (0,1,0)) — um `type="text"` sairia com o raio e
                 *      a borda do CSS legado, sem erro nenhum. Aquele arquivo é
                 *      importado por 18 telas e não pode ser apagado nesta frente.
                 */
                type="tel"
                inputMode="numeric"
                autoComplete="off"
                value={documento}
                onChange={(evento) => setDocumento(formatCpfCnpj(evento.target.value))}
                onKeyDown={(evento) => {
                  if (evento.key === 'Enter') void buscar();
                }}
                maxLength={18}
                placeholder="000.000.000-00"
                className="tw-w-full tw-appearance-none tw-rounded-xl tw-border tw-border-superficie-borda tw-bg-white tw-px-3 tw-py-2.5 tw-font-corpo tw-text-sm tw-tabular-nums tw-text-texto-corpo placeholder:tw-text-texto-desabilitado focus:tw-border-brand focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-brand/20"
              />

              <Botao
                variante="primario"
                carregando={estado === 'buscando'}
                disabled={!podeBuscar}
                onClick={() => void buscar()}
                className="sm:tw-shrink-0"
              >
                {estado === 'buscando' ? 'Buscando...' : 'Buscar'}
              </Botao>
            </div>

            <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-text-texto-suave">
              Esta consulta mostra também os veículos substituídos e excluídos — é o histórico
              completo do associado.
            </p>
          </Card>

          {estado === 'nao-encontrado' && (
            <EmptyState
              titulo="Nenhum associado com esse documento"
              descricao="Confira os números digitados. O cadastro pode existir só no SGA e ainda não ter sido importado."
              icone="busca"
            />
          )}

          {estado === 'erro' && (
            <ErrorState
              mensagem="Não foi possível consultar agora."
              detalhe={erro}
              aoTentarNovamente={() => void buscar()}
            />
          )}

          {estado === 'achou' && associado && (
            <>
              <Card className="tw-p-4">
                <h2 className="tw-mb-3 tw-mt-0 tw-font-marca tw-text-base tw-font-bold tw-text-texto-titulo">
                  Dados do associado
                </h2>
                <dl className="tw-m-0 tw-grid tw-grid-cols-1 tw-gap-x-6 tw-gap-y-2 sm:tw-grid-cols-2">
                  <Linha rotulo="Nome" valor={associado.nomeCompleto} />
                  <Linha rotulo="CPF/CNPJ" valor={formatCpfCnpj(String(associado.cpfCnpj || ''))} />
                  <Linha rotulo="E-mail" valor={associado.email} />
                  <Linha rotulo="RG" valor={associado.rg} />
                  <Linha rotulo="Data de nascimento" valor={dataBr(associado.dataNascimento)} />
                  <Linha rotulo="Sexo" valor={associado.sexo} />
                  <Linha rotulo="Celular" valor={celular(associado)} />
                  <Linha rotulo="Regional" valor={associado.regionalNome} />
                  <Linha rotulo="Endereço" valor={endereco(associado)} larga />
                </dl>
              </Card>

              {carros.length > 0 ? (
                <div className="tw-flex tw-flex-col tw-gap-3">
                  <h2 className="tw-m-0 tw-font-marca tw-text-base tw-font-bold tw-text-texto-titulo">
                    {carros.length === 1 ? 'Veículo' : `Veículos (${carros.length})`}
                  </h2>

                  {carros.map((carro: any, indice: number) => {
                    // Situação do veículo (at-37/item 39): a substituição tem
                    // estado próprio e o registro não some da consulta.
                    const situacao = rotuloSituacaoCarro(carro);

                    return (
                      <Card key={carro.id || indice} className="tw-p-4">
                        <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                          <span className="tw-font-marca tw-text-sm tw-font-bold tw-text-texto-titulo">
                            {carro.placa}
                          </span>
                          <Chip variante={situacao.variante}>{situacao.texto}</Chip>
                        </div>

                        <dl className="tw-m-0 tw-grid tw-grid-cols-1 tw-gap-x-6 tw-gap-y-2 sm:tw-grid-cols-2">
                          <Linha rotulo="Modelo" valor={carro.modelo} />
                          <Linha rotulo="Ano modelo" valor={carro.anoModelo} />
                          <Linha rotulo="Ano de fabricação" valor={carro.anoFabricacao} />
                          <Linha rotulo="Chassi" valor={carro.chassi} />
                          <Linha rotulo="Renavam" valor={carro.renavam} />
                          <Linha
                            rotulo="Categoria"
                            valor={carro.objCategorias?.descricao_categoria || carro.categoria}
                          />
                          <Linha
                            rotulo="Cor"
                            valor={
                              carro.objcor?.descricao || carro.objcor?.descricao_cor || carro.cor
                            }
                          />
                          <Linha
                            rotulo="Combustível"
                            valor={
                              carro.objCombustivel?.descricao ||
                              carro.objCombustivel?.descricao_combustivel ||
                              carro.combustivel
                            }
                          />
                          <Linha rotulo="Câmbio" valor={carro.cambio} />
                          {/* Motocicleta elétrica (item 50) não tem FIPE — sem o
                              fallback o rótulo aparecia sozinho, sem valor. */}
                          <Linha rotulo="Valor FIPE" valor={moeda(carro.valorFipe)} />
                          <Linha rotulo="Valor protegido" valor={moeda(carro.valorProtegido)} />
                          <Linha rotulo="Endereço" valor={endereco(carro)} larga />
                        </dl>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <EmptyState
                  titulo="Nenhum veículo neste cadastro"
                  descricao="O associado existe, mas não tem veículo registrado no SAAC."
                />
              )}
            </>
          )}
        </div>
      </div>
    </ShellSaac>
  );
};

/** Uma linha de `rótulo: valor`. Valor ausente vira "—", nunca lacuna em branco. */
const Linha: React.FC<{ rotulo: string; valor?: React.ReactNode; larga?: boolean }> = ({
  rotulo,
  valor,
  larga = false,
}) => {
  const vazio = valor === null || valor === undefined || valor === '';
  return (
    <div className={larga ? 'sm:tw-col-span-2' : undefined}>
      <dt className="tw-text-[11px] tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
        {rotulo}
      </dt>
      <dd className="tw-m-0 tw-text-sm tw-text-texto-corpo [overflow-wrap:anywhere]">
        {vazio ? '—' : valor}
      </dd>
    </div>
  );
};

const dataBr = (iso?: string): string => {
  if (!iso) return '';
  const data = new Date(iso);
  return Number.isNaN(data.getTime()) ? '' : data.toLocaleDateString('pt-BR');
};

const celular = (associado: Associado): string => {
  if (!associado.celular) return '';
  return associado.dddCelular ? `(${associado.dddCelular}) ${associado.celular}` : associado.celular;
};

const endereco = (registro: any): string => {
  if (!registro?.rua) return '';
  const complemento = registro.complemento ? ` - ${registro.complemento}` : '';
  return `${registro.rua}, ${registro.numero}${complemento}, ${registro.bairro}, ${registro.cidade} - ${registro.estado}, CEP ${registro.cep}`;
};

const moeda = (valor?: number | null): string =>
  valor === null || valor === undefined || valor === 0
    ? ''
    : valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export default AdesaoConsultar;
